"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.adminReleasesListInput = exports.releasesListInput = exports.releaseDeleteInput = exports.releaseUpdateInput = exports.releaseGetByIdInput = exports.releaseCreateInput = exports.RELEASE_LIST_LIMIT_MAX = exports.RELEASE_LIST_LIMIT_DEFAULT = exports.RELEASE_CURSOR_MAX = exports.RELEASE_SOURCE_URL_MAX = exports.RELEASE_NOTE_MAX = exports.RELEASE_TITLE_MAX = exports.RELEASE_RANGE_MAX_DAYS = void 0;
exports.parseShanghaiCivilDate = parseShanghaiCivilDate;
exports.shanghaiCivilDate = shanghaiCivilDate;
exports.shanghaiYearMonth = shanghaiYearMonth;
exports.shanghaiMonthBounds = shanghaiMonthBounds;
exports.storedReleaseDate = storedReleaseDate;
const zod_1 = require("zod");
const metrics_1 = require("./metrics");
/** Same rule as `slugSchema`. Kept here so this module does not import the zod barrel. */
const categorySlugInput = zod_1.z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'slug_format');
/** Inclusive civil-day span for `releases.list`. One day past a leap year is rejected. */
exports.RELEASE_RANGE_MAX_DAYS = 366;
exports.RELEASE_TITLE_MAX = 120;
exports.RELEASE_NOTE_MAX = 500;
exports.RELEASE_SOURCE_URL_MAX = 2048;
/**
 * Opaque keyset cursors are `base64url(YYYY-MM-DD + "|" + uuid)`, 63 characters.
 * One extra character still fits; anything longer is a field error, not a gateway 431.
 * A request line the gateway rejects with 431 or 414 is left as that status.
 */
exports.RELEASE_CURSOR_MAX = 64;
exports.RELEASE_LIST_LIMIT_DEFAULT = 50;
exports.RELEASE_LIST_LIMIT_MAX = 100;
const CIVIL_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
/**
 * `YYYY-MM-DD` as a civil date in Asia/Shanghai.
 * Shanghai has no daylight-saving shift, so the label is the UTC calendar day
 * stored in `@db.Date`. Slashes, dots, and instants are rejected.
 */
function parseShanghaiCivilDate(value) {
    const trimmed = value.trim();
    if (!CIVIL_DAY.test(trimmed))
        return null;
    return (0, metrics_1.utcCalendarDate)(trimmed);
}
function datePart(parts, type) {
    for (const part of parts) {
        if (part.type === type)
            return part.value;
    }
    return '';
}
/** Civil `YYYY-MM-DD` of an instant in Asia/Shanghai. */
function shanghaiCivilDate(instant) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Shanghai',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(instant);
    const year = datePart(parts, 'year');
    const month = datePart(parts, 'month');
    const day = datePart(parts, 'day');
    if (!year || !month || !day) {
        throw new Error('Asia/Shanghai date parts missing');
    }
    return `${year}-${month}-${day}`;
}
/** `YYYY-MM` of an instant in Asia/Shanghai. */
function shanghaiYearMonth(instant) {
    return shanghaiCivilDate(instant).slice(0, 7);
}
/**
 * Closed civil interval of a calendar month in Asia/Shanghai.
 * `2026-11` is `2026-11-01` through `2026-11-30`.
 */
function shanghaiMonthBounds(month) {
    const match = /^(\d{4})-(\d{2})$/.exec(month);
    if (!match)
        return null;
    const year = Number(match[1]);
    const monthNumber = Number(match[2]);
    if (year < 1 || year > 9999 || monthNumber < 1 || monthNumber > 12)
        return null;
    const from = new Date(Date.UTC(year, monthNumber - 1, 1));
    const to = new Date(Date.UTC(year, monthNumber, 0));
    return {
        from: (0, metrics_1.formatUtcCalendarDate)(from),
        to: (0, metrics_1.formatUtcCalendarDate)(to),
    };
}
/** MONTH precision stores the first civil day. DAY keeps the validated day. */
function storedReleaseDate(value, precision) {
    const parsed = parseShanghaiCivilDate(value);
    if (!parsed)
        return null;
    if (precision === 'DAY')
        return parsed;
    return new Date(Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), 1));
}
function isHttpsUrl(value) {
    if (!value.startsWith('https://') || /\s/.test(value))
        return false;
    const host = value.slice('https://'.length).split('/')[0] ?? '';
    return host.length > 0;
}
const releaseDateInput = zod_1.z
    .string()
    .trim()
    .superRefine((value, ctx) => {
    if (!parseShanghaiCivilDate(value)) {
        ctx.addIssue({ code: 'custom', message: 'invalid_date' });
    }
});
const releaseRegionInput = zod_1.z
    .union([zod_1.z.string(), zod_1.z.null()])
    .optional()
    .transform((value, ctx) => {
    if (value === undefined || value === null)
        return value;
    if (value === 'CN' || value === 'JP' || value === 'GLOBAL')
        return value;
    ctx.addIssue({ code: 'custom', message: 'release_region' });
    return zod_1.z.NEVER;
});
const releaseSourceUrlInput = zod_1.z
    .union([zod_1.z.string().trim().max(exports.RELEASE_SOURCE_URL_MAX), zod_1.z.null()])
    .optional()
    .transform((value, ctx) => {
    if (value === undefined || value === null)
        return value;
    if (!isHttpsUrl(value)) {
        ctx.addIssue({ code: 'custom', message: 'source_url' });
        return zod_1.z.NEVER;
    }
    return value;
});
const releaseNoteInput = zod_1.z
    .union([zod_1.z.string().trim().max(exports.RELEASE_NOTE_MAX, 'release_note_too_long'), zod_1.z.null()])
    .optional()
    .transform((value) => {
    if (value === undefined || value === null)
        return value;
    return value.length === 0 ? null : value;
});
exports.releaseCreateInput = zod_1.z.object({
    title: zod_1.z.string().trim().min(1, 'title_required').max(exports.RELEASE_TITLE_MAX, 'title_too_long'),
    releaseDate: releaseDateInput,
    datePrecision: zod_1.z.enum(['DAY', 'MONTH']).default('DAY'),
    region: releaseRegionInput,
    seriesId: zod_1.z.union([zod_1.z.string().uuid(), zod_1.z.null()]).optional(),
    categoryId: zod_1.z.union([zod_1.z.string().uuid(), zod_1.z.null()]).optional(),
    note: releaseNoteInput,
    sourceUrl: releaseSourceUrlInput,
});
exports.releaseGetByIdInput = zod_1.z.object({
    id: zod_1.z.string().uuid(),
});
exports.releaseUpdateInput = exports.releaseCreateInput.extend({
    id: zod_1.z.string().uuid(),
});
const releaseDeleteReason = zod_1.z
    .string()
    .trim()
    .max(500, { message: 'delete_reason_too_long' })
    .optional();
exports.releaseDeleteInput = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    reason: releaseDeleteReason,
});
const releaseListLimit = zod_1.z.number().int().min(1).max(exports.RELEASE_LIST_LIMIT_MAX);
const releaseCursorInput = zod_1.z.string().min(1).max(exports.RELEASE_CURSOR_MAX);
/**
 * Public range. `from` and `to` are required Asia/Shanghai civil dates.
 * The interval is closed. A span of 366 days is allowed; 367 is not.
 * `limit` defaults to 50 so an omitted page size cannot scan the whole table.
 */
exports.releasesListInput = zod_1.z
    .object({
    from: zod_1.z.string().trim(),
    to: zod_1.z.string().trim(),
    categorySlug: categorySlugInput.optional(),
    cursor: releaseCursorInput.optional(),
    limit: releaseListLimit.default(exports.RELEASE_LIST_LIMIT_DEFAULT),
})
    .superRefine((value, ctx) => {
    const from = parseShanghaiCivilDate(value.from);
    const to = parseShanghaiCivilDate(value.to);
    if (!from) {
        ctx.addIssue({ code: 'custom', path: ['from'], message: 'invalid_date' });
    }
    if (!to) {
        ctx.addIssue({ code: 'custom', path: ['to'], message: 'invalid_date' });
    }
    if (!from || !to)
        return;
    if (to.getTime() < from.getTime()) {
        ctx.addIssue({ code: 'custom', path: ['to'], message: 'release_range_order' });
        return;
    }
    if ((0, metrics_1.inclusiveUtcDayCount)(from, to) > exports.RELEASE_RANGE_MAX_DAYS) {
        ctx.addIssue({ code: 'custom', path: ['to'], message: 'release_range_too_long' });
    }
});
/** Admin list. Limit defaults to 50 and is never clamped into range. */
exports.adminReleasesListInput = zod_1.z.object({
    cursor: releaseCursorInput.optional(),
    limit: releaseListLimit.default(exports.RELEASE_LIST_LIMIT_DEFAULT),
});
//# sourceMappingURL=release.js.map